import { useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import { ReactComponent as Logo } from '../censuszen-logo.svg';
import '../styles/LandingPage.css';

/* SVG icons as small components to keep JSX readable. All are decorative. */
const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 8H12.5M12.5 8L8.5 4M12.5 8L8.5 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <polyline points="3.5 8 6.5 11 12.5 5"/>
  </svg>
);

const FileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
);

const ClipboardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/>
  </svg>
);

const PillIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <rect x="3" y="8" width="18" height="8" rx="4" transform="rotate(-45 12 12)"/><line x1="8.5" y1="15.5" x2="15.5" y2="8.5"/>
  </svg>
);

const FlagIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 22V4a1 1 0 011-1h13l-3 5 3 5H5"/>
  </svg>
);

const IdIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="12" r="2.5"/><line x1="14" y1="10" x2="18" y2="10"/><line x1="14" y1="14" x2="18" y2="14"/>
  </svg>
);

const DocIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const DEMO_FILES = [
  { name: 'Hospital_Discharge_Summary.pdf', detail: 'PDF · 14 pages', icon: <FileIcon />, tone: 'pdf' },
  { name: 'F2F_Encounter_Note.pdf', detail: 'PDF · 2 pages', icon: <ClipboardIcon />, tone: 'clinical' },
  { name: 'Physician_Order.pdf', detail: 'PDF · 3 pages', icon: <FileIcon />, tone: 'pdf' },
  { name: 'Med_Reconciliation.pdf', detail: 'PDF · 5 pages', icon: <PillIcon />, tone: 'lab' },
];

const DEMO_FACTS = [
  { label: 'Face-to-face', value: '04/30/2026, within window', highlight: true },
  { label: 'Homebound', value: 'Documented', highlight: true },
  { label: 'Signed order', value: 'Present', highlight: true },
  { label: 'Primary Dx', value: 'Acute decompensated heart failure' },
  { label: 'Insurance', value: 'Medicare A' },
];

const DEMO_REPORT_LINES = [
  'Eligible for home health under Medicare A.',
  'Face-to-face within the window. Homebound documented.',
  'Signed order present. No critical flags.',
];

/* Animated demo. Manages the step-label active class via useEffect. */
function HeroDemo() {
  const uploadRef = useRef(null);
  const extractRef = useRef(null);
  const reviewRef = useRef(null);

  const runCycle = useCallback(() => {
    const labels = [uploadRef.current, extractRef.current, reviewRef.current];
    const clear = () => labels.forEach(l => l?.classList.remove('active'));

    clear();
    uploadRef.current?.classList.add('active');

    const t1 = setTimeout(() => { clear(); extractRef.current?.classList.add('active'); }, 3500);
    const t2 = setTimeout(() => { clear(); reviewRef.current?.classList.add('active'); }, 7500);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    let cleanup = runCycle();
    const interval = setInterval(() => {
      cleanup();
      cleanup = runCycle();
    }, 12000);
    return () => { cleanup(); clearInterval(interval); };
  }, [runCycle]);

  return (
    <div className="hero-demo">
      <div className="demo-window">
        <div className="demo-titlebar">
          <span className="demo-dot" />
          <span className="demo-dot" />
          <span className="demo-dot" />
          <span className="demo-titlebar-text">Referral packet &middot; Julie Doe</span>
        </div>

        <div className="demo-body">
          {/* Progress pips */}
          <div className="demo-steps">
            <div className="demo-step-pip" />
            <div className="demo-step-pip" />
            <div className="demo-step-pip" />
          </div>
          <div className="demo-step-labels">
            <span className="demo-step-label" ref={uploadRef}>Upload</span>
            <span className="demo-step-label" ref={extractRef}>Extract</span>
            <span className="demo-step-label" ref={reviewRef}>Review</span>
          </div>

          {/* Scan line */}
          <div className="extract-scan" />

          {/* Phase 1: the packet uploads */}
          <div className="demo-phase demo-upload">
            {DEMO_FILES.map(({ name, detail, icon, tone }) => (
              <div className="doc-card" key={name}>
                <div className={`doc-icon doc-icon--${tone}`}>{icon}</div>
                <div className="doc-meta">
                  <div className="doc-name">{name}</div>
                  <div className="doc-detail">{detail}</div>
                </div>
                <div className="doc-check"><CheckIcon /></div>
              </div>
            ))}
          </div>

          {/* Phase 2: facts extracted */}
          <div className="demo-phase demo-extract">
            <div className="extract-grid">
              {DEMO_FACTS.map(({ label, value, highlight }) => (
                <div className="extract-field" key={label}>
                  <div className="extract-label">{label}</div>
                  <div className={highlight ? 'extract-value extract-value--highlight' : 'extract-value'}>
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Phase 3: eligibility report and RN briefing */}
          <div className="demo-phase demo-approve">
            <div className="approve-hpi">
              <div className="approve-hpi-header">
                <div className="approve-hpi-icon"><DocIcon /></div>
                <span className="approve-hpi-title">Pre-SOC eligibility report</span>
              </div>
              <div className="approve-hpi-lines">
                {DEMO_REPORT_LINES.map((line) => (
                  <div className="hpi-line" key={line}>{line}</div>
                ))}
              </div>
            </div>
            <div className="approve-badge">
              <div className="approve-badge-check"><CheckIcon size={16} /></div>
              <div>
                <div className="approve-badge-text">Review complete</div>
                <div className="approve-badge-sub">Ready for RN briefing</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="hero-demo-caption">A referral packet, read page by page.</p>
    </div>
  );
}

const GUIDE_POINTS = [
  {
    icon: <DocIcon />,
    text: 'Every fact comes with its source page. Click it and the page opens.',
  },
  {
    icon: <ClipboardIcon />,
    text: 'The face-to-face window and the homebound documentation are checked against CMS rules.',
  },
  {
    icon: <FlagIcon />,
    text: 'Twenty red flag checks run on every packet. A missing signed order, a face-to-face outside the window, and a patient who does not qualify are flagged as critical.',
  },
  {
    icon: <IdIcon />,
    text: 'A patient identity check reads the name, date of birth, and MRN on every document and stops a packet that mixes two patients.',
  },
];

const PLAN_STEPS = [
  {
    title: 'Upload the referral packet.',
    detail: 'PDF or Word, straight from the fax or the portal.',
  },
  {
    title: 'Review the flagged gaps and the facts.',
    detail: 'Each one links to the page it came from.',
  },
  {
    title: 'Send the RN briefing and start care.',
    detail: 'The pre-visit briefing and the eligibility report are ready to share.',
  },
];

const STAKES_WITHOUT = [
  'A claim denied for a face-to-face dated outside the window.',
  'A nurse who walks into the first visit without the medication list.',
  "A packet with two patients' records and no one who noticed.",
];

const STAKES_WITH = [
  'An accept-or-decline decision in minutes, with the reasons written down.',
  'Clean start-of-care paperwork the day the referral arrives.',
  'The nurse briefed before the visit. Diagnosis codes ready for PDGM.',
];

export default function LandingPage() {
  return (
    <div className="landing-page">
      {/* --- Navigation --- */}
      <nav className="landing-nav">
        <Logo className="landing-nav-logo" aria-label="CensusZen" />
        <div className="landing-nav-links">
          <Link to="/pricing" className="landing-nav-link landing-nav-link--ghost">
            Pricing
          </Link>
          <Link to="/faq" className="landing-nav-link landing-nav-link--ghost">
            FAQ
          </Link>
          <Link to="/login" className="landing-nav-link landing-nav-link--solid">
            Log in
          </Link>
        </div>
      </nav>

      {/* --- Hero --- */}
      <section className="hero">
        <div className="hero-copy">
          <h1 className="hero-headline">
            Turn a referral packet into a decision <em>before lunch.</em>
          </h1>
          <p className="hero-subtitle">
            Home health agencies use CensusZen to turn a referral packet into an
            accept-or-decline decision, an RN briefing, and PDGM-ready codes in minutes.
          </p>
          <div className="hero-actions">
            <Link to="/request-access?source=demo_request" className="btn-primary">
              Book a demo <ArrowIcon />
            </Link>
            <Link to="/request-access?source=trial_request" className="btn-ghost">
              Contact us for a free trial
            </Link>
          </div>
        </div>

        <HeroDemo />
      </section>

      {/* --- Problem --- */}
      <section className="landing-section problem-section">
        <div className="landing-section-inner">
          <h2 className="landing-section-title">The packet is the problem</h2>
          <ul className="problem-list">
            <li>Forty pages of fax and portal downloads, and the face-to-face note is on page thirty-one.</li>
            <li>Accept the patient and you might not get paid. Decline and a patient who qualifies waits.</li>
            <li>The queue does not stop while you read.</li>
          </ul>
        </div>
      </section>

      {/* --- Guide --- */}
      <section className="landing-section guide-section">
        <div className="landing-section-inner">
          <h2 className="landing-section-title">We have read those packets too</h2>
          <p className="landing-section-lead">
            CensusZen reads every page so you can decide with the facts in front of you.
          </p>
          <ul className="guide-list">
            {GUIDE_POINTS.map(({ icon, text }) => (
              <li className="guide-item" key={text}>
                <div className="guide-item-icon">{icon}</div>
                <p>{text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* --- Plan --- */}
      <section className="landing-section plan-section">
        <div className="landing-section-inner">
          <h2 className="landing-section-title">Three steps to a start of care</h2>
          <ol className="plan-steps">
            {PLAN_STEPS.map(({ title, detail }) => (
              <li className="plan-step" key={title}>
                <p className="plan-step-title">{title}</p>
                <p className="plan-step-detail">{detail}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* --- Stakes --- */}
      <section className="landing-section stakes-section">
        <div className="landing-section-inner">
          <h2 className="landing-section-title">What changes</h2>
          <div className="stakes-grid">
            <div className="stakes-column stakes-column--without">
              <h3>Without CensusZen</h3>
              {STAKES_WITHOUT.map((line) => <p key={line}>{line}</p>)}
            </div>
            <div className="stakes-column stakes-column--with">
              <h3>With CensusZen</h3>
              {STAKES_WITH.map((line) => <p key={line}>{line}</p>)}
            </div>
          </div>
        </div>
      </section>

      {/* --- Final CTA --- */}
      <section className="landing-cta-section">
        <div className="landing-cta-card">
          <h2>See it on one of your packets.</h2>
          <p>Bring a referral packet to the demo. We process it while you watch.</p>
          <div className="landing-cta-buttons">
            <Link to="/request-access?source=demo_request" className="landing-cta-primary">
              Book a demo
            </Link>
            <Link to="/request-access?source=trial_request" className="landing-cta-secondary">
              Contact us for a free trial
            </Link>
          </div>
        </div>
      </section>

      {/* --- Footer --- */}
      <Footer variant="landing" />
    </div>
  );
}
